/* eslint-disable import/prefer-default-export */

import mirror from 'keymirror';
import debounce from 'debounce-async';
import Progress from '../lib/progress';
import segment from '../core/segment';
import { getPercentCompleted } from '../components/ProgressList/progress-list-utils';

export const actionTypes = mirror({
  PROGRESS_GET: null,
  PROGRESS_GET_COUNTS: null,
  PROGRESS_GET_RECENT: null,
  PROGRESS_GET_RECENTLY_VIEWED: null,
  PROGRESS_GET_LEVELS_COMPLETED: null,
  PATHS_PROGRESS_UPDATED: null,
  COURSES_PROGRESS_UPDATED: null,
  LESSONS_PROGRESS_UPDATED: null,
  COMPLETED_LESSONS_UPDATED: null,
  PROGRESS_GET_ERROR: null,
  PROGRESS_IN_PROGRESS: null,
  PROGRESS_UPDATE_ERROR: null,
  FC_SIGNOUT: null,
  SET_MARK_COMPLETE_MODAL: null,
  SET_LEVEL: null,
  STAND_ALONE_LESSON_PROGRESS_UPDATE: null,
});

function progressInProgress() {
  return {
    type: actionTypes.PROGRESS_IN_PROGRESS,
  };
}

function getProgress(progress) {
  return {
    type: actionTypes.PROGRESS_GET,
    progress,
  };
}

function getProgressLevelsCompletedUpdated(completed) {
  return {
    type: actionTypes.PROGRESS_GET_LEVELS_COMPLETED,
    completed,
  };
}

function getProgressCountsUpdated(counts) {
  return {
    type: actionTypes.PROGRESS_GET_COUNTS,
    counts,
  };
}

function getProgressRecentUpdated(recent) {
  return {
    type: actionTypes.PROGRESS_GET_RECENT,
    recent,
  };
}

function getRecentlyViewedUpdated(recentlyViewed) {
  return {
    type: actionTypes.PROGRESS_GET_RECENTLY_VIEWED,
    recentlyViewed,
  };
}

function getProgressError(error) {
  return {
    type: actionTypes.PROGRESS_GET_ERROR,
    error,
  };
}

function pathsProgressUpdated(
  pathID,
  playlistID,
  courseID,
  lessonID,
  style,
  instrument,
  courseType,
  lessonType,
) {
  return {
    type: actionTypes.PATHS_PROGRESS_UPDATED,
    pathID,
    playlistID,
    courseID,
    lessonID,
    style,
    instrument,
    courseType,
    lessonType,
  };
}

function coursesProgressUpdated(courseID, lessonID, courseType, lessonType) {
  return {
    type: actionTypes.COURSES_PROGRESS_UPDATED,
    courseID,
    lessonID,
    courseType,
    lessonType,
  };
}

function standAloneLessonProgressUpdate(lessonID, lessonType) {
  return {
    type: actionTypes.STAND_ALONE_LESSON_PROGRESS_UPDATE,
    lessonID,
    lessonType,
  };
}

function lessonsProgressUpdated(lessonID, lessonType, videoWatchedSeconds = 0, completed = false) {
  return {
    type: actionTypes.LESSONS_PROGRESS_UPDATED,
    lessonID,
    lessonType,
    videoWatchedSeconds,
    completed,
  };
}

function completedLessonsUpdated(options) {
  return {
    type: actionTypes.COMPLETED_LESSONS_UPDATED,
    options,
  };
}

function progressUpdateError(error) {
  return {
    type: actionTypes.PROGRESS_UPDATE_ERROR,
    error,
  };
}

function updateRecentLevel(level) {
  return {
    type: actionTypes.SET_LEVEL,
    level,
  };
}

export async function requestProgress(listOfID, type, dispatch) {
  try {
    dispatch(progressInProgress());
    let progress = null;
    if (type === 'lessons') {
      progress = await Progress.getLessons(listOfID);
    } else if (type === 'courses') {
      progress = await Progress.getCourses(listOfID);
    } else {
      progress = await Progress.getPath(listOfID);
    }
    dispatch(getProgress(progress));
  } catch (err) {
    dispatch(getProgressError(err));
  }
}

export function getProgressLevelsCompleted(pathArr) {
  return async dispatch => {
    try {
      dispatch(progressInProgress());
      const completed = await Progress.getProgressLevelsCompleted(pathArr);
      dispatch(getProgressLevelsCompletedUpdated(completed));
    } catch (err) {
      dispatch(getProgressError(err));
    }
  };
}

export function getProgressCounts(progressArr) {
  // eslint-disable-line
  return async dispatch => {
    try {
      dispatch(progressInProgress());
      const counts = await Progress.getProgressCounts(progressArr);
      dispatch(getProgressCountsUpdated(counts));
    } catch (err) {
      dispatch(getProgressError(err));
    }
  };
}

export function getProgressRecent(lessons, exercises, songs, chords, riffs) {
  return async dispatch => {
    try {
      dispatch(progressInProgress());
      const counts = await Progress.getProgressRecent(lessons, exercises, songs, chords, riffs);
      dispatch(getProgressRecentUpdated(counts));
    } catch (err) {
      dispatch(getProgressError(err));
    }
  };
}

export function getRecentlyViewed(count) {
  return async dispatch => {
    try {
      dispatch(progressInProgress());
      const counts = await Progress.getRecentlyViewed(count);
      dispatch(getRecentlyViewedUpdated(counts['recently-viewed'] || []));
    } catch (err) {
      dispatch(getProgressError(err));
    }
  };
}

export function trackProgressSnapshot(lessonId) {
  return async (dispatch, getState) => {
    try {
      const progress = await Progress.getProgressCounts();
      const { fenderConnect, myProgress, myPath } = getState() || {};
      const { completed } = myProgress;
      let { lessons } = myProgress;
      const playlistID = completed['path-levels-completed'][0]['playlist-id'];
      const currentPlaylist = myPath.playlists.filter(playlist => playlist.id === playlistID)[0];
      const currentLevel = currentPlaylist['description-long'];
      if (!lessons[lessonId]) {
        lessons = { ...lessons, lessonId: { completed: 100 } };
      }
      const mergedLessons = currentPlaylist.courses.map(c => c.lessons);
      const percent = getPercentCompleted(mergedLessons, lessons) * 100;
      const lessonsCompletedPercent = percent < 0 ? Math.ceil(percent) : Math.floor(percent);
      if ('userID' in fenderConnect && 'chords-count' in progress) {
        const properties = {
          fcId: fenderConnect.userID,
          siteId: process.env.REACT_APP_CLIENT_SITE_ID,
          chords: progress['chords-count'],
          courses: progress['courses-count'],
          lessons: progress['lessons-count'],
          levels: progress['levels-count'],
          skills: progress['skills-count'],
          songs_riffs: progress['songs-riffs-count'],
          lessonsCompletedPercent,
          currentLevel,
        };
        segment.event('Progress', properties);
      }
    } catch (err) {
      dispatch(getProgressError(err));
    }
  };
}

export function getCoursesProgress(listOfID) {
  return async dispatch => {
    try {
      dispatch(progressInProgress());
      const progress = await Progress.getCourses(listOfID);
      dispatch(getProgress(progress));
    } catch (err) {
      dispatch(getProgressError(err));
    }
  };
}

export function getLessonsProgress(listOfID) {
  return async dispatch => {
    try {
      dispatch(progressInProgress());
      const progress = await Progress.getLessons(listOfID);
      dispatch(getProgress(progress));
    } catch (err) {
      dispatch(getProgressError(err));
    }
  };
}

export function updatePathProgress(
  pathID,
  playlistID,
  courseID,
  lessonID,
  style,
  instrument,
  courseType,
  lessonType,
) {
  const pathUpdate = async dispatch => {
    try {
      const paths = [
        Progress.mapUpdatePathPayload(
          pathID,
          playlistID,
          courseID,
          lessonID,
          style,
          instrument,
          courseType,
          lessonType,
        ),
      ];
      await Progress.updatePath(paths);
      dispatch(
        pathsProgressUpdated(
          pathID,
          playlistID,
          courseID,
          lessonID,
          style,
          instrument,
          courseType,
          lessonType,
        ),
      );
    } catch (err) {
      dispatch(progressUpdateError(err));
    }
  };
  return debounce(pathUpdate, 3000);
}

export function updateCourseProgress(
  courseID,
  lessonID,
  courseType,
  lessonType,
  instrument = 'guitar',
) {
  return async dispatch => {
    try {
      const courses = [
        Progress.mapUpdateCoursePayload(courseID, lessonID, courseType, lessonType, instrument),
      ];
      await Progress.updateCourse(courses);
      dispatch(coursesProgressUpdated(courseID, lessonID, courseType, lessonType));
    } catch (err) {
      dispatch(progressUpdateError(err));
    }
  };
}

export function updateLessonProgress(
  lessonID,
  lessonType,
  videoWatchedSeconds,
  instrument = 'guitar',
  videoTotalSeconds,
) {
  return async dispatch => {
    try {
      const percentCompleted = Math.round((videoWatchedSeconds / videoTotalSeconds) * 100);
      const lessons = [
        Progress.mapUpdateLessonPayload(
          lessonID,
          lessonType,
          videoWatchedSeconds,
          percentCompleted,
          instrument,
        ),
      ];
      await Progress.updateLesson(lessons);
      dispatch(
        lessonsProgressUpdated(lessonID, lessonType, videoWatchedSeconds, videoWatchedSeconds),
      );
    } catch (err) {
      dispatch(progressUpdateError(err));
    }
  };
}

export function updateLessonStandaloneProgress(lessonID, lessonType) {
  return async dispatch => {
    try {
      await Progress.updateLessonStandalone(lessonID, lessonType);
      dispatch(standAloneLessonProgressUpdate(lessonID, lessonType));
    } catch (err) {
      dispatch(progressUpdateError(err));
    }
  };
}

export function setMarkCompleteModal(options) {
  return {
    type: actionTypes.SET_MARK_COMPLETE_MODAL,
    options,
  };
}

export function updateLessonCompletedProgress(lessons) {
  return async dispatch => {
    try {
      await Progress.updateLessonCompletedProgress(lessons);
      dispatch(completedLessonsUpdated(lessons));
      dispatch(setMarkCompleteModal({}));
    } catch (err) {
      dispatch(progressUpdateError(err));
    }
  };
}

export function updateLevel(level) {
  return async dispatch => {
    dispatch(updateRecentLevel(level));
  };
}
